const BASE_URL = import.meta.env.VITE_API_URL

/** Erro de borda da API, já com o status HTTP preservado. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * Corpo da resposta de erro, quando for JSON. Quase sempre irrelevante — o
     * backend responde com `include-message: never` —, mas a submissão devolve
     * em 422 os erros por chave da revalidação, e sem eles o requerente
     * não tem o que corrigir.
     */
    readonly corpo?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Access token do Keycloak; ausente em rotas públicas. */
  token?: string | undefined
  body?: unknown
  /**
   * Corpo cru enviado tal e qual, sem serialização JSON — para o upload vetorial, cujo
   * conteúdo é o arquivo em si (o backend recebe `byte[]`). Requer {@link contentType}; ignora
   * {@link body}.
   */
  rawBody?: BodyInit
  /** Content-Type do {@link rawBody} (ex.: `application/octet-stream`). */
  contentType?: string
}

/**
 * Cliente HTTP fino. Não engole erro: qualquer status fora de 2xx vira ApiError,
 * que o TanStack Query converte em estado de erro visível na UI.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { token, body, rawBody, contentType, headers, ...rest } = options

  const corpoBruto = rawBody !== undefined
  // O boundary do multipart é gerado pelo próprio browser; declarar o
  // Content-Type à mão o omitiria e o backend não conseguiria separar as partes.
  const multipart = rawBody instanceof FormData
  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(corpoBruto && !multipart
        ? { 'Content-Type': contentType ?? 'application/octet-stream' }
        : {}),
      ...(!corpoBruto && body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token === undefined ? {} : { Authorization: `Bearer ${token}` }),
      ...headers,
    },
    ...(corpoBruto ? { body: rawBody } : body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!response.ok) {
    // O backend responde com include-message: never, então não há detalhe
    // sensível para propagar — usamos o status como fonte da mensagem. O corpo
    // vem junto quando for JSON; ler falha em resposta vazia, e aí não há corpo.
    const corpo: unknown = await response.json().catch(() => undefined)
    throw new ApiError(
      response.status,
      `Falha na requisição a ${path} (HTTP ${response.status})`,
      corpo
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

/**
 * Variante que devolve o corpo como `Blob` — o download de anexo, cujo
 * conteúdo é o arquivo em si e não JSON.
 *
 * Existe como irmã de `apiFetch` em vez de opção dela porque o tipo de retorno
 * muda: espremer as duas na mesma assinatura custaria um genérico que mente
 * sobre o que volta. O tratamento de erro é o mesmo, e continua valendo a regra
 * de não chamar `fetch` fora deste módulo.
 */
export async function apiFetchBlob(
  path: string,
  options: Omit<RequestOptions, 'body' | 'rawBody' | 'contentType'> = {}
): Promise<{ blob: Blob; nome: string | undefined }> {
  const { token, headers, ...rest } = options

  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      ...(token === undefined ? {} : { Authorization: `Bearer ${token}` }),
      ...headers,
    },
  })

  if (!response.ok) {
    const corpo: unknown = await response.json().catch(() => undefined)
    throw new ApiError(
      response.status,
      `Falha na requisição a ${path} (HTTP ${response.status})`,
      corpo
    )
  }

  return { blob: await response.blob(), nome: nomeDoContentDisposition(response) }
}

/** Nome de arquivo do `Content-Disposition`, quando o backend o declarou. */
function nomeDoContentDisposition(response: Response): string | undefined {
  const cabecalho = response.headers.get('Content-Disposition')
  if (!cabecalho) return undefined
  const codificado = /filename\*=UTF-8''([^;]+)/i.exec(cabecalho)
  if (codificado?.[1]) return decodeURIComponent(codificado[1])
  return /filename="?([^";]+)"?/i.exec(cabecalho)?.[1]
}
